import { Fragment } from "react";
import { FollowUpFlagIcon, formatFollowUpTime } from "shared/app/CallFollowUp";
import { formatCallerQuote } from "shared/app/CallFollowUp/callerQuote";
import { Show } from "shared/components";
import { cx } from "shared/utils/classnames";
import type { Conversation } from "../types";
import { useCallTranscript } from "../api/hooks";
import { followUpCallElementId } from "../context/followUpCallContext";
import { useInboxEmbed } from "../context/useInboxEmbed";
import { conversationFollowUpLabel } from "../utils/followUp";
import { ClearFlagButton } from "./ClearFlagButton";

const Separator = () => <span className="mx-1.5">·</span>;

// Says why the thread is pinned: whoever opens it may not own the lead.
export const FollowUpConversationBanner = ({
  conversation
}: {
  conversation: Conversation;
}) => {
  const compact = Boolean(useInboxEmbed()?.compact);
  const callId = conversation.follow_up_call_transcript_id;
  // What the caller asked, so the reader knows what to answer (CRM-1872).
  const { data } = useCallTranscript(callId);
  const quote = formatCallerQuote(data?.caller_quote);
  const details = [
    "Phone call",
    formatFollowUpTime(conversation.follow_up_open_at),
    "clears when you reply"
  ].filter(Boolean);

  return (
    <div
      role="status"
      className={cx(
        "flex items-center gap-3 border-b border-danger-100 bg-danger-50 py-2 text-xs font-medium text-danger-700",
        compact ? "px-4" : "px-6"
      )}
    >
      <FollowUpFlagIcon />
      {/* Only the quote shrinks: the reason and the details stay readable. */}
      <span className="flex min-w-0 items-center truncate whitespace-nowrap">
        <span className="font-bold">
          {conversationFollowUpLabel(conversation)}
        </span>
        <Show if={Boolean(quote)}>
          <Separator />
          <span className="min-w-0 truncate" title={quote ?? undefined}>
            {quote}
          </span>
        </Show>
        {details.map((part) => (
          <Fragment key={part}>
            <Separator />
            <span>{part}</span>
          </Fragment>
        ))}
      </span>
      <span className="ml-auto flex shrink-0 items-center gap-3">
        <Show if={Boolean(callId)}>
          <button
            type="button"
            className="font-bold text-danger-700 hover:underline"
            onClick={() =>
              callId &&
              document
                .getElementById(followUpCallElementId(callId))
                ?.scrollIntoView({ behavior: "smooth", block: "center" })
            }
          >
            See the call
          </button>
        </Show>
        <ClearFlagButton conversationId={conversation.id} kind="follow_up" />
      </span>
    </div>
  );
};
